import React, { useRef, lazy, memo, Component } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { BookOpen, CheckSquare, ArrowRight, ShoppingCart } from 'lucide-react';
import { HeatWaveEffect } from './HeatWaveEffect';
// Optimized animation variants - reduced complexity
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (custom: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
      delay: custom * 0.1
    }
  })
};
const scaleIn = {
  hidden: {
    scale: 0.98,
    opacity: 0
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const CHECKOUT_URL = 'https://thewisdomlab.online/cart/56448720798071:1?checkout';
// Memoized CTA Component
const CTAButton = memo(({
  className = '',
  size = 'default'
}: {
  className?: string;
  size?: 'default' | 'large';
}) => {
  const buttonClasses = size === 'large' ? 'px-10 py-5 text-lg' : 'px-8 py-4 text-base';
  return <div className={`flex flex-col items-center gap-3 ${className}`} data-id="element-1">
        <a href={CHECKOUT_URL} className={`group inline-flex items-center gap-3 ${buttonClasses} bg-terracotta-500 text-sand-50 rounded-full font-medium transition-all duration-300 hover:bg-terracotta-600 shadow-lg hover:shadow-xl`} data-id="element-2">
          Get Everything for $19.99
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" data-id="element-3" />
        </a>
        <div className="text-center space-y-1" data-id="element-4">
          <p className="text-sm text-sand-500" data-id="element-5">
            Instant digital access • All bonuses included
          </p>
          <p className="text-xs text-sand-400" data-id="element-6">Regular price $49.99</p>
        </div>
      </div>;
});
CTAButton.displayName = 'CTAButton';
export function DesertLanding() {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });
  const yHero = useTransform(scrollYProgress, [0, 0.5], [0, 100]);
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  return <div ref={containerRef} className="relative w-full min-h-screen bg-sand-50 text-terracotta-900 font-sans overflow-hidden bg-noise selection:bg-terracotta-200 selection:text-terracotta-900" data-id="element-7">
      {/* Progress Line */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-terracotta-500 origin-left z-50 opacity-50" style={{
      scaleX
    }} data-id="element-8" />

      {/* STICKY HEADER CTA - ALWAYS VISIBLE */}
      <header className="fixed top-0 left-0 right-0 bg-sand-50/95 backdrop-blur-md border-b border-terracotta-200/50 z-40 shadow-lg" data-id="element-9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-4" data-id="element-10">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0" data-id="element-11">
            <span className="font-serif text-lg sm:text-xl md:text-2xl text-terracotta-900 truncate" data-id="element-12">
              Ancient Laws of Abundance
            </span>
            <div className="hidden sm:flex items-center gap-2" data-id="element-13">
              <span className="text-sm text-sand-500 line-through" data-id="element-14">$49.99</span>
              <span className="text-xl md:text-2xl font-serif text-terracotta-600" data-id="element-15">
                $19.99
              </span>
            </div>
          </div>
          <a href={CHECKOUT_URL} className="group flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-terracotta-500 text-sand-50 rounded-full font-medium transition-all duration-300 hover:bg-terracotta-600 shadow-lg hover:shadow-xl text-sm sm:text-base whitespace-nowrap" data-id="element-16">
            <ShoppingCart className="w-4 h-4" data-id="element-17" />
            <span className="hidden xs:inline" data-id="element-18">Get for $19.99</span>
            <span className="xs:hidden" data-id="element-19">Buy</span>
          </a>
        </div>
      </header>

      {/* Add padding top to account for fixed header */}
      <div className="h-16 sm:h-20" data-id="element-20" />

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center px-4 sm:px-6 py-16 md:py-24 overflow-hidden" data-id="element-21">
        <HeatWaveEffect data-id="element-22" />

        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-23">
          {/* Left: Text Content */}
          <motion.div style={{
          y: yHero
        }} className="space-y-6 md:space-y-8" data-id="element-24">
            <motion.div variants={fadeUp} custom={0} initial="hidden" animate="visible" className="space-y-4 md:space-y-6" data-id="element-25">
              <blockquote className="font-serif text-xl sm:text-2xl md:text-3xl italic text-terracotta-700 leading-relaxed" data-id="element-26">
                "Abundance doesn't fail. It collapses when it's built without
                soul."
              </blockquote>
            </motion.div>

            <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="visible" className="font-serif text-4xl sm:text-5xl md:text-7xl font-light leading-[1.1] tracking-tight text-terracotta-900" data-id="element-27">
              Ancient Laws of Abundance
            </motion.h1>

            <motion.p variants={fadeUp} custom={2} initial="hidden" animate="visible" className="text-lg sm:text-xl md:text-2xl text-sand-600 font-light leading-relaxed" data-id="element-28">
              Jewish Wisdom for Wealth, Peace, and Purpose
            </motion.p>

            <motion.p variants={fadeUp} custom={2.5} initial="hidden" animate="visible" className="text-base sm:text-lg text-sand-500 font-light" data-id="element-29">
              By Rabbi Eliahu Ben Ori
            </motion.p>

            {/* Pricing Box */}
            <motion.div variants={fadeUp} custom={3} initial="hidden" animate="visible" className="inline-block p-4 sm:p-6 bg-terracotta-50 border-2 border-terracotta-200 rounded-lg" data-id="element-30">
              <div className="flex items-baseline gap-3 mb-2" data-id="element-31">
                <span className="text-base sm:text-lg text-sand-500 line-through" data-id="element-32">
                  $49.99
                </span>
                <span className="text-3xl sm:text-4xl font-serif text-terracotta-600" data-id="element-33">
                  $19.99
                </span>
              </div>
              <p className="text-sm font-medium mb-3 text-terracotta-700" data-id="element-34">
                🎁 Today Only: Includes 2 FREE Bonuses
              </p>
              <div className="text-xs sm:text-sm text-sand-600 space-y-1" data-id="element-35">
                <p data-id="element-36">✓ Workbook (Value: $19.99) - FREE</p>
                <p data-id="element-37">✓ Weekly Checklist (Value: $9.99) - FREE</p>
                <p className="font-medium text-terracotta-900 pt-2" data-id="element-38">
                  Total Value: $79.97 → Today: $19.99
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} custom={4} initial="hidden" animate="visible" className="flex justify-center lg:justify-start" data-id="element-39">
              <CTAButton data-id="element-40" />
            </motion.div>
          </motion.div>

          {/* Right: Book Image - Optimized for LCP */}
          <motion.div variants={scaleIn} initial="hidden" animate="visible" className="relative" data-id="element-41">
            <img src="https://cdn.magicpatterns.com/uploads/iZ3j16nQ6vXVVHHE6fJy3q/books.png" alt="Ancient Laws of Abundance - Book Bundle" className="w-full h-auto drop-shadow-2xl" loading="eager" fetchpriority="high" width="800" height="600" data-id="element-42" />
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 max-w-4xl mx-auto" data-id="element-43">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="space-y-8 md:space-y-12 text-center" data-id="element-44">
          <motion.h2 variants={fadeUp} custom={0} className="font-serif text-3xl sm:text-4xl md:text-5xl text-terracotta-900 leading-tight" data-id="element-45">
            You work hard. You think deeply. You try to live correctly.
          </motion.h2>

          <motion.p variants={fadeUp} custom={1} className="text-lg sm:text-xl md:text-2xl text-sand-600 font-light leading-relaxed" data-id="element-46">
            And yet… If you're honest, there's a quiet instability beneath it
            all.
          </motion.p>

          <motion.p variants={fadeUp} custom={2} className="text-base sm:text-lg text-sand-500 leading-relaxed max-w-2xl mx-auto" data-id="element-47">
            Not always visible. Not dramatic. Just a subtle sense that what
            you're building feels heavier than it should… or strangely empty
            once you reach it.
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className="pt-6 md:pt-8" data-id="element-48">
            <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl text-terracotta-700 mb-4 md:mb-6" data-id="element-49">
              The truth almost no one is taught
            </h3>
            <p className="text-lg sm:text-xl text-sand-600 leading-relaxed max-w-3xl mx-auto" data-id="element-50">
              Abundance doesn't disappear because of bad luck. It doesn't vanish
              because of poor tactics. It doesn't fail because you didn't try
              hard enough.
            </p>
            <p className="text-xl sm:text-2xl md:text-3xl font-serif text-terracotta-900 mt-6 md:mt-8 italic" data-id="element-51">
              It collapses when the internal structure holding it is disordered.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} custom={4} className="pt-6 md:pt-8" data-id="element-52">
            <CTAButton data-id="element-53" />
          </motion.div>
        </motion.div>
      </section>

      {/* ORIGIN STORY - RABBI SECTION */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 bg-terracotta-50/30" data-id="element-54">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-55">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-56">
            <img src="https://cdn.magicpatterns.com/uploads/rLUTQbx7umgRKYXnxJjdzK/74cb954b-61b1-4a6b-8294-7bba0f96de85.jpg" alt="Rabbi Eliahu Ben Ori" className="w-full h-auto rounded-lg shadow-2xl" loading="lazy" width="600" height="800" data-id="element-57" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="space-y-4 md:space-y-6" data-id="element-58">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-terracotta-900 leading-tight" data-id="element-59">
              Where these teachings actually come from
            </h2>

            <div className="space-y-3 md:space-y-4 text-base sm:text-lg text-sand-600 leading-relaxed" data-id="element-60">
              <p data-id="element-61">
                When Eliahu was thirteen, sitting quietly in a synagogue, an
                elderly man he had never seen before approached him.
              </p>
              <p data-id="element-62">
                He placed a small notebook—wrapped in dark cloth—into his hands.
              </p>
              <p className="text-lg sm:text-xl font-serif italic text-terracotta-700" data-id="element-63">
                "Do not open this to impress anyone. Open it when you are ready
                to obey."
              </p>
              <p data-id="element-64">Inside were no promises. No affirmations. No miracles.</p>
              <p className="text-xl sm:text-2xl font-serif text-terracotta-900" data-id="element-65">
                Only instructions.
              </p>
            </div>

            <div className="pt-4 md:pt-6 flex justify-center lg:justify-start" data-id="element-66">
              <CTAButton data-id="element-67" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 max-w-5xl mx-auto" data-id="element-68">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="space-y-12 md:space-y-16 text-center" data-id="element-69">
          <div className="space-y-6 md:space-y-8" data-id="element-70">
            <motion.h2 variants={fadeUp} custom={0} className="font-serif text-4xl sm:text-5xl md:text-6xl text-terracotta-900 leading-tight" data-id="element-71">
              The Ancient Laws of Abundance
            </motion.h2>

            <motion.p variants={fadeUp} custom={1} className="text-lg sm:text-xl md:text-2xl text-sand-600 font-light leading-relaxed max-w-3xl mx-auto" data-id="element-72">
              This is not motivation. Not religion. Not manifestation.
            </motion.p>

            <motion.p variants={fadeUp} custom={2} className="text-base sm:text-lg text-sand-500 leading-relaxed max-w-3xl mx-auto" data-id="element-73">
              It is a timeless internal system, rooted in ancient wisdom, where:
            </motion.p>
          </div>

          <motion.div variants={fadeUp} custom={3} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 text-left max-w-4xl mx-auto" data-id="element-74">
            <PrincipleCard text="Character is established before capital" data-id="element-75" />
            <PrincipleCard text="Energy is protected before it expands" data-id="element-76" />
            <PrincipleCard text="Time is ordered before freedom is pursued" data-id="element-77" />
            <PrincipleCard text="Money follows consciousness—not the other way around" data-id="element-78" />
          </motion.div>

          <motion.p variants={fadeUp} custom={4} className="text-xl sm:text-2xl md:text-3xl font-serif italic text-terracotta-700 pt-6 md:pt-8" data-id="element-79">
            "Abundance is not quantity. It is alignment."
          </motion.p>

          <motion.div variants={fadeUp} custom={5} className="pt-6 md:pt-8" data-id="element-80">
            <CTAButton data-id="element-81" />
          </motion.div>
        </motion.div>
      </section>

      {/* WHAT CHANGES SECTION */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 bg-sand-100/50" data-id="element-82">
        <div className="max-w-4xl mx-auto" data-id="element-83">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-8 md:space-y-12 text-center" data-id="element-84">
            <motion.h2 variants={fadeUp} custom={0} className="font-serif text-3xl sm:text-4xl md:text-5xl text-terracotta-900 leading-tight" data-id="element-85">
              What begins to change (quietly, steadily)
            </motion.h2>

            <motion.p variants={fadeUp} custom={1} className="text-lg sm:text-xl text-sand-600 leading-relaxed" data-id="element-86">
              Those who apply these laws often notice:
            </motion.p>

            <motion.div variants={fadeUp} custom={2} className="space-y-4 md:space-y-6 text-left max-w-2xl mx-auto" data-id="element-87">
              <BenefitItem text="Calm, grounded financial decisions" data-id="element-88" />
              <BenefitItem text="Discipline without burnout" data-id="element-89" />
              <BenefitItem text="Progress without frantic motion" data-id="element-90" />
              <BenefitItem text="Peace while still building more" data-id="element-91" />
              <BenefitItem text="Alignment between values, money, and purpose" data-id="element-92" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 max-w-6xl mx-auto" data-id="element-93">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="space-y-12 md:space-y-16" data-id="element-94">
          <div className="text-center space-y-4 md:space-y-6" data-id="element-95">
            <motion.h2 variants={fadeUp} custom={0} className="font-serif text-3xl sm:text-4xl md:text-5xl text-terracotta-900 leading-tight" data-id="element-96">
              Included to ensure this isn't just read—but lived
            </motion.h2>

            <motion.div variants={fadeUp} custom={1} className="inline-block p-4 sm:p-6 bg-terracotta-500 text-sand-50 rounded-lg" data-id="element-97">
              <p className="text-xl sm:text-2xl font-serif mb-2" data-id="element-98">
                🎁 2 FREE Bonuses Included
              </p>
              <p className="text-base sm:text-lg" data-id="element-99">
                Total Bonus Value: $29.98 - Yours FREE Today
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-100">
            <BonusCard image="https://cdn.magicpatterns.com/uploads/w54seiEZ2LsNZaERG1iEZX/ChatGPT_Image_2_ene_2026,_13_44_52.png" icon={<BookOpen className="w-6 h-6" data-id="element-102" />} title="Ancient Laws of Abundance — Workbook" value="$19.99" description="Because insight without integration changes nothing." features={['Guided exercises', 'Deep reflection prompts', 'Practical daily application', 'Clear structure without overwhelm']} data-id="element-101" />

            <BonusCard image="https://cdn.magicpatterns.com/uploads/heZ9YrrStJp7UxCx9NAZSk/ChatGPT_Image_2_ene_2026,_13_46_36.png" icon={<CheckSquare className="w-6 h-6" data-id="element-104" />} title="Weekly Prosperity Checklist" value="$9.99" description="A grounding tool to:" features={['Protect your energy', 'Maintain internal order', 'Turn intention into habit']} data-id="element-103" />
          </div>

          <motion.div variants={fadeUp} custom={2} className="pt-6 md:pt-8" data-id="element-105">
            <CTAButton size="large" data-id="element-106" />
          </motion.div>
        </motion.div>
      </section>

      {/* WHY DIFFERENT SECTION */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 bg-terracotta-900/5" data-id="element-107">
        <div className="max-w-4xl mx-auto text-center space-y-8 md:space-y-12" data-id="element-108">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="font-serif text-3xl sm:text-4xl md:text-5xl text-terracotta-900 leading-tight" data-id="element-109">
            Why this feels different (and is)
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg sm:text-xl text-sand-600 leading-relaxed" data-id="element-110">
            These teachings are not trends. They are not hacks.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="space-y-4 md:space-y-6 text-base sm:text-lg text-sand-500" data-id="element-111">
            <p data-id="element-112">They have been:</p>
            <div className="space-y-3 md:space-y-4 max-w-2xl mx-auto" data-id="element-113">
              <p data-id="element-114">Observed across generations</p>
              <p data-id="element-115">Applied by merchants, parents, leaders, and seekers</p>
              <p data-id="element-116">Refined through lived experience—not theory</p>
            </div>
            <p className="text-xl sm:text-2xl font-serif text-terracotta-900 pt-6 md:pt-8" data-id="element-117">
              They promise no shortcuts. Only foundations.
            </p>
          </motion.div>

          <motion.blockquote initial={{
          opacity: 0,
          scale: 0.98
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="text-xl sm:text-2xl md:text-3xl font-serif italic text-terracotta-700 pt-6 md:pt-8" data-id="element-118">
            "I do not promise miracles. I promise clarity. And clarity is the
            beginning of all true abundance."
          </motion.blockquote>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 max-w-5xl mx-auto" data-id="element-119">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="space-y-8 md:space-y-12" data-id="element-120">
          <motion.h2 variants={fadeUp} custom={0} className="font-serif text-3xl sm:text-4xl md:text-5xl text-terracotta-900 text-center leading-tight" data-id="element-121">
            What readers quietly report
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-122">
            <TestimonialCard quote="It didn't just help my finances. It gave me peace." data-id="element-123" />
            <TestimonialCard quote="For the first time, I understood why things kept collapsing." data-id="element-124" />
            <TestimonialCard quote="It didn't motivate me. It reordered me." data-id="element-125" />
          </div>

          <motion.div variants={fadeUp} custom={1} className="pt-6 md:pt-8" data-id="element-126">
            <CTAButton data-id="element-127" />
          </motion.div>
        </motion.div>
      </section>

      {/* FAQ SECTION */}
      <section className="relative py-16 md:py-32 px-4 sm:px-6 max-w-3xl mx-auto" data-id="element-128">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="space-y-8 md:space-y-12" data-id="element-129">
          <motion.h2 variants={fadeUp} custom={0} className="font-serif text-3xl sm:text-4xl md:text-5xl text-terracotta-900 text-center leading-tight" data-id="element-130">
            Honest questions
          </motion.h2>

          <div className="space-y-6 md:space-y-8" data-id="element-131">
            <FAQItem question="Is this religious?" answer="No. No belief required. Only application." data-id="element-132" />
            <FAQItem question="Is this for beginners?" answer="Especially. Foundations matter most before growth." data-id="element-133" />
            <FAQItem question="What if I'm already successful but restless?" answer="This was written for that exact moment." data-id="element-134" />
          </div>
        </motion.div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="relative py-24 md:py-40 px-4 sm:px-6 flex flex-col items-center justify-center text-center bg-terracotta-500 text-sand-50 overflow-hidden" data-id="element-135">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-136">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/10 rounded-full" data-id="element-137" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/10 rounded-full" data-id="element-138" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-white/10 rounded-full" data-id="element-139" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={scaleIn} className="relative z-10 max-w-3xl space-y-6 md:space-y-8" data-id="element-140">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-6xl mb-4 md:mb-6 text-sand-50 leading-tight" data-id="element-141">
            This was not written to inspire you. It was written to be followed.
          </h2>

          <p className="text-sand-100/90 text-base sm:text-lg md:text-xl font-light leading-relaxed" data-id="element-142">
            And if you've done everything "right"… yet something still feels
            unstable beneath your success… That's not coincidence.
          </p>

          <p className="text-lg sm:text-xl md:text-2xl text-sand-50 font-light" data-id="element-143">
            This work tends to find people at a very specific moment.
          </p>

          {/* Final pricing emphasis */}
          <div className="py-6 md:py-8 px-4 sm:px-6 bg-sand-50/10 backdrop-blur-sm rounded-lg border border-sand-50/20" data-id="element-144">
            <div className="flex items-baseline justify-center gap-3 mb-3" data-id="element-145">
              <span className="text-lg sm:text-xl text-sand-200 line-through" data-id="element-146">
                $49.99
              </span>
              <span className="text-4xl sm:text-5xl font-serif text-sand-50" data-id="element-147">
                $19.99
              </span>
            </div>
            <p className="text-sand-100 mb-2 text-sm sm:text-base" data-id="element-148">
              🎁 Includes 2 FREE Bonuses (Value: $29.98)
            </p>
            <p className="text-xs sm:text-sm text-sand-200" data-id="element-149">
              Complete Package • Instant Access • Today Only
            </p>
          </div>

          <div className="flex flex-col items-center gap-3 pt-4 md:pt-6" data-id="element-150">
            <a href={CHECKOUT_URL} className="inline-flex items-center gap-3 bg-sand-50 text-terracotta-600 px-8 sm:px-10 py-4 sm:py-5 rounded-full font-medium text-base sm:text-lg tracking-wide hover:bg-white transition-colors shadow-xl shadow-terracotta-900/20" data-id="element-151">
              Get Everything for $19.99
              <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" data-id="element-152" />
            </a>
            <div className="text-center space-y-1" data-id="element-153">
              <p className="text-sm text-sand-100" data-id="element-154">
                Instant digital access • All bonuses included
              </p>
              <p className="text-xs text-sand-200" data-id="element-155">Regular price $49.99</p>
            </div>
          </div>

          <p className="text-sand-100/80 text-sm sm:text-base pt-4 md:pt-6 max-w-2xl mx-auto" data-id="element-156">
            Stop chasing abundance. Become ordered enough to hold it.
          </p>
        </motion.div>
      </section>
    </div>;
}
// Memoized components for better performance
const PrincipleCard = memo(({
  text
}: {
  text: string;
}) => {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="p-4 sm:p-6 border-l-2 border-terracotta-400 bg-sand-50/50" data-id="element-157">
      <p className="text-base sm:text-lg text-terracotta-900 leading-relaxed" data-id="element-158">
        {text}
      </p>
    </motion.div>;
});
PrincipleCard.displayName = 'PrincipleCard';
const BenefitItem = memo(({
  text
}: {
  text: string;
}) => {
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4
  }} className="flex items-start gap-3 sm:gap-4" data-id="element-159">
      <div className="mt-1.5 w-2 h-2 rounded-full bg-terracotta-500 flex-shrink-0" data-id="element-160" />
      <p className="text-base sm:text-lg text-sand-600" data-id="element-161">{text}</p>
    </motion.div>;
});
BenefitItem.displayName = 'BenefitItem';
const BonusCard = memo(({
  image,
  icon,
  title,
  value,
  description,
  features
}: {
  image: string;
  icon: React.ReactNode;
  title: string;
  value: string;
  description: string;
  features: string[];
}) => {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="space-y-4 sm:space-y-6 relative" data-id="element-162">
        {/* FREE Badge */}
        <div className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 bg-terracotta-500 text-sand-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium shadow-lg text-xs sm:text-sm" data-id="element-163">
          FREE (Value: {value})
        </div>

        <div className="relative overflow-hidden rounded-lg shadow-xl" data-id="element-164">
          <img src={image} alt={title} className="w-full h-auto" loading="lazy" width="500" height="500" data-id="element-165" />
        </div>

        <div className="space-y-3 sm:space-y-4" data-id="element-166">
          <div className="flex items-center gap-2 sm:gap-3 text-terracotta-600" data-id="element-167">
            {icon}
            <h3 className="font-serif text-xl sm:text-2xl text-terracotta-900" data-id="element-168">
              {title}
            </h3>
          </div>

          <p className="text-sand-600 leading-relaxed text-sm sm:text-base" data-id="element-169">
            {description}
          </p>

          <ul className="space-y-1.5 sm:space-y-2" data-id="element-170">
            {features.map((feature, index) => <li key={index} className="flex items-start gap-2 sm:gap-3" data-id="element-171">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-terracotta-400 flex-shrink-0" data-id="element-172" />
                <span className="text-sand-600 text-sm sm:text-base" data-id="element-173">
                  {feature}
                </span>
              </li>)}
          </ul>
        </div>
      </motion.div>;
});
BonusCard.displayName = 'BonusCard';
const TestimonialCard = memo(({
  quote
}: {
  quote: string;
}) => {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="p-6 sm:p-8 bg-sand-50 border border-terracotta-200 rounded-lg" data-id="element-174">
      <p className="text-base sm:text-lg text-terracotta-900 italic leading-relaxed" data-id="element-175">
        "{quote}"
      </p>
    </motion.div>;
});
TestimonialCard.displayName = 'TestimonialCard';
const FAQItem = memo(({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4
  }} className="space-y-2 sm:space-y-3 text-left" data-id="element-176">
        <h3 className="font-serif text-xl sm:text-2xl text-terracotta-900" data-id="element-177">
          {question}
        </h3>
        <p className="text-base sm:text-lg text-sand-600 leading-relaxed" data-id="element-178">
          {answer}
        </p>
      </motion.div>;
});
FAQItem.displayName = 'FAQItem';